import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import {
    AppShellProvider,
    ConfigRegistryProvider,
    // initRouteTracking,
    LoaderProvider,
    type AppShellState,
    registerTranslations,
    ServerTimeProvider,
    configureTimezoneAbbreviation,
    TacModalProvider,
    registerConfig,
    globalState,
    DialogProvider,
    initServerTimeSync,
    initializeAnalytics,
} from "mfe-lib-bfo-shared";
import { QueryProvider, usePreLoginHttp } from "mfe-lib-bfo-shared/query";
import { App } from "./App";
import "./index.css";
import { ensureI18n } from "./i18n/i18n";
import { AppConfig, modules } from "./config/app.config";
import { i18nConfig } from "./i18n/config";
import { AlertDialog, ConfirmDialog } from "dssp-ui-lib";
import { LoadingSpinner } from "dssp-ui-lib";
import "dssp-ui-lib/dist/styles/index.css";
import "dssp-ui-lib/dist/styles/main.css";
import { registerPreLoginDecorators } from "./functions/query/registerPreLoginDecorators";

// In a real deployment this comes from the session/auth service.
const shellState: AppShellState = {
    user: { id: "u-001", name: "Test", roles: ["RM", "MAKER"] },
    locale: "en-MY",
    applicationRef: "APP-2026-000123",
};

// Per-lib configuration, one entry per federated remote, keyed by the
// remote's package name (each remote's config.ts documents its shape).
// Entries are partial — anything omitted falls back to the lib's defaults.
const libConfigs = {
    "mfe-lib-bfo-status-tracker": {
        showCompletedDates: true,
        allowManualAdvance: true,
    },
    "mfe-lib-bfo-cl-application": {
        currencies: ["MYR", "USD", "SGD", "EUR"],
        defaultCurrency: "MYR",
        minAmount: 10_000,
        maxTenorMonths: 120,
    },
    "mfe-lib-bfo-app-status-tracker": {
        showCompletedDates: true,
        allowManualAdvance: true,
    },
};

const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

//initRouteTracking();

// Load runtime config and point the shared query layer at this environment's
// API BEFORE rendering, so no query fires against an unconfigured client. The
// host owns this decision; the value comes from /config.json at deploy time.
export async function bootstrap() {
    configureTimezoneAbbreviation("MYT");
    await AppConfig.load();
    const i18n = await ensureI18n();
    initializeAnalytics();

    if (!import.meta.env.DEV && !AppConfig.getEnableLog()) {
        console.log = () => {};
    }

    // Load the environment-specific API origin before creating HTTP connectors.
    registerPreLoginDecorators(i18n);
    const { httpClient } = usePreLoginHttp();
    initServerTimeSync(httpClient, "Asia/Kuala_Lumpur");

    registerConfig({ translation: i18nConfig, modules: { ...modules } });

    globalState.set("isCiModeOptionsAvailable", AppConfig.getIsCiModeAvailable());
    globalState.set("baseUrl", AppConfig.getBaseUrl());
    globalState.set("tacMaxRetry", 3);

    // if (AppConfig.getIsCiMode()) {
    //     document.body.classList.add("debug-keys-active");
    // }

    try {
        await registerTranslations(
            i18n,
            "stitcher",
            AppConfig.getTranslationLanguages(),
            AppConfig.loadLanguageResource,
        );
    } catch (error: unknown) {
        //TODO tempfix
        console.warn("[host] session translations could not be registered; rendering with base translations:", error);
    }

    createRoot(document.getElementById("root")!).render(
        <StrictMode>
            <I18nextProvider i18n={i18n}>
                <AppShellProvider value={shellState}>
                    <QueryProvider>
                        <ConfigRegistryProvider configs={libConfigs}>
                            <BrowserRouter basename={routerBasename}>
                                <LoaderProvider renderLoader={() => <LoadingSpinner visible={true} />}>
                                    <ServerTimeProvider>
                                        <TacModalProvider>
                                            <DialogProvider
                                                renderDialog={(dialogProps, isOpen, closeDialog) =>
                                                    dialogProps.leftBtnLabel ? (
                                                        <ConfirmDialog
                                                            isOpen={isOpen}
                                                            setOpen={closeDialog}
                                                            iconType={dialogProps.iconType}
                                                            title={dialogProps.title}
                                                            message={dialogProps.message}
                                                            leftBtnLabel={dialogProps.leftBtnLabel}
                                                            onCancelClick={dialogProps.onCancelClick}
                                                            rightBtnLabel={dialogProps.rightBtnLabel}
                                                            onConfirmClick={dialogProps.onConfirmClick}
                                                            width={dialogProps.width}
                                                            isHTMLContent={dialogProps.isHTMLContent}
                                                            htmlMessage={dialogProps.htmlMessage}
                                                        />
                                                    ) : (
                                                        <AlertDialog
                                                            isOpen={isOpen}
                                                            setOpen={closeDialog}
                                                            iconType={dialogProps.iconType}
                                                            title={dialogProps.title}
                                                            message={dialogProps.message}
                                                            btnLabel={dialogProps.rightBtnLabel}
                                                            onClick={dialogProps.onConfirmClick}
                                                            isHTMLContent={dialogProps.isHTMLContent}
                                                            htmlMessage={dialogProps.htmlMessage}
                                                        />
                                                    )
                                                }
                                            >
                                                <App i18n={i18n} />
                                            </DialogProvider>
                                        </TacModalProvider>
                                    </ServerTimeProvider>
                                </LoaderProvider>
                            </BrowserRouter>
                        </ConfigRegistryProvider>
                    </QueryProvider>
                </AppShellProvider>
            </I18nextProvider>
        </StrictMode>,
    );
}

if (import.meta.env.MODE !== "test") {
    void bootstrap();
}
